import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { EVENT_FAQ_ANSWERS_CONST, EVENT_FAQ_CONST } from './constants';

const miscEventFaqDefinitions = defineMessages({
  eventFaqTocHeader: {
    id: 'shared.faq_form.eventFaqTocHeader',
    defaultMessage: 'Set up your FAQ'
  },
  eventFaqTocSubheader: {
    id: 'shared.faq_form.eventFaqTocSubheader',
    defaultMessage: 'Answer frequently asked questions and rules about event bookings'
  },
  eventFaqNux: {
    id: 'shared.faq_form.eventFaqNux',
    defaultMessage: 'Set up your FAQ'
  },
  next: {
    id: 'shared.faq_form.next_button_text',
    defaultMessage: 'Next'
  },
  exit: {
    id: 'shared.faq_form.exit_button_text',
    defaultMessage: 'Save and exit'
  },
  place_settings_subheader: {
    id: 'shared.faq_form.place_settings_subheader',
    defaultMessage: 'Plates, utensils, etc.'
  },
  serving_utensils_subheader: {
    id: 'shared.faq_form.serving_utensils_subheader',
    defaultMessage: 'Serving spoons, tongs, etc..'
  },
  text_area_placeholder: {
    id: 'shared.faq_form.text_area_description',
    defaultMessage: 'Add a short description...'
  },
  other_food_rules_placeholder: {
    id: 'shared.faq_form.other_food_rules_placeholder',
    defaultMessage: 'Please describe your rules regarding food and non-alcoholic beverages...'
  },
  other_food_prep_rules_placeholder: {
    id: 'shared.faq_form.other_food_prep_rules_placeholder',
    defaultMessage: 'Please describe your rules regarding cooking and food preparation...'
  },
  place_setting_tooltip: {
    id: 'shared.faq_form.place_setting_tooltip',
    defaultMessage: 'Maximum amount of guests the available place settings can accommodate.'
  },
  cups_tooltip: {
    id: 'shared.faq_form.cups_tooltip.cups_tooltip',
    defaultMessage: 'Amount of cups available to guests during their booking.'
  },
  wine_glasses_tooltip: {
    id: 'shared.faq_form.wine_glasses_tooltip',
    defaultMessage: 'Amount of wine glasses available to guests during their booking.'
  },
  alcohol_consumption_placeholder: {
    id: 'shared.faq_form.alcohol_consumption_placeholder',
    defaultMessage: 'Please describe your rules regarding alcohol consumption...'
  },
  tables_tooltip: {
    id: 'shared.faq_form.tables_tooltip',
    defaultMessage: 'Total tables. Describe types of tables and seating capacity in the description below.'
  },
  chairs_tooltip: {
    id: 'shared.faq_form.chairs_tooltip',
    defaultMessage: 'Total chairs. Describe styles of chairs in the description below.'
  },
  soft_seating_tooltip: {
    id: 'shared.faq_form.soft_seating_tooltip',
    defaultMessage: 'Maximum seating capacity for your soft seating (e.g. a sofa that seats 3, plus 2 lounge chairs, would be a seating capacity of 5). '
  },
  restroom_tooltip: {
    id: 'shared.faq_form.restroom_tooltip',
    defaultMessage: 'Amount of restrooms (including stalls) available for guests during their booking.'
  },
  other_av_equipment_placeholder: {
    id: 'shared.faq_form.other_av_equipment',
    defaultMessage: 'Please describe any other AV equipment...'
  },
  other_activities_placeholder: {
    id: 'shared.faq_form.other_activities_placeholder',
    defaultMessage: 'Please describe any other activities available in your space...'
  },
  other_prohibited_activities_placeholder: {
    id: 'shared.faq_form.other_prohibited_activities_placeholder',
    defaultMessage: 'Please describe your any other activities you’d prefer to not host...'
  },
  other_prohibited_activities_tips: {
    id: 'shared.faq_form.other_prohibited_activities_tips',
    defaultMessage: 'We\'ll show these as activities you\'d prefer to not host. Guests can still book them, so monitor and choose to accept or decline bookings.'
  },
  other_prohibited_activities_tips_guest: {
    id: 'shared.faq_form.other_prohibited_activities_tips_guest',
    defaultMessage: 'Hosts may not accept your booking if you choose to book for these activities.'
  },
  other_event_rules_placeholder: {
    id: 'shared.faq_form.other_event_rules_placeholder',
    defaultMessage: 'Please describe any other rules regarding event bookings...'
  },
  food_rules: {
    id: 'shared.event_faq.section.food_rules',
    defaultMessage: 'Rules for food and non-alcoholic beverages'
  },
  food_prep_rules: {
    id: 'shared.event_faq.section.food_prep_rules',
    defaultMessage: 'Rules for cooking and food preparation'
  },
  quiet_hours_header: {
    id: 'shared.event_rules_form.quiet_hours_header',
    defaultMessage: 'Does your space have quiet hours?*'
  },
  flexible_hours: {
    id: 'shared.event_rules_form.flexible_hours',
    defaultMessage: 'Are your bookable hours flexible? '
  },
  available_av_equipment: {
    id: 'shared.event_rules_form.available_av_equipment',
    defaultMessage: 'Available AV equipment'
  },
  av_rules: {
    id: 'shared.event_rules_form.av_rules',
    defaultMessage: 'Rules for AV and music'
  },
  event_rules_header: {
    id: 'shared.event_rules_form.event_rules_header',
    defaultMessage: 'Event related rules and restrictions'
  },
  activities_not_permitted_header: {
    id: 'listing.event_faq.section.activities_not_permitted',
    defaultMessage: 'Events this space does not wish to host'
  }
});

const subtitles = defineMessages({
  kitchen_amenities: {
    id: 'shared.event_faq.section.kitchen_amenities',
    defaultMessage: 'Kitchen Amenities'
  },
  available_kitchen_setups: {
    id: 'shared.event_faq.section.kitchen_setups',
    defaultMessage: 'Available kitchen setups'
  },
  restrooms_and_facilities: {
    id: 'shared.event_faq.section.restrooms_and_facilities',
    defaultMessage: 'Restrooms and facilities'
  },
  tables: {
    id: 'shared.event_faq.section.tables',
    defaultMessage: 'Tables'
  },
  chairs: {
    id: 'shared.event_faq.section.chairs',
    defaultMessage: 'Chairs'
  },
  soft_seating: {
    id: 'shared.event_faq.section.soft_seating',
    defaultMessage: 'Soft Seating'
  }
});

const category_labels = defineMessages({
  food_and_beverage: {
    id: 'shared.event_faq.section.food_beverage',
    defaultMessage: 'Food and Beverage'
  },
  restrooms: {
    id: 'shared.event_faq.section.restrooms',
    defaultMessage: 'Restrooms'
  },
  alcohol: {
    id: 'shared.event_faq.section.alcohol',
    defaultMessage: 'Alcoholic Beverages'
  },
  furniture: {
    id: 'shared.event_faq.section.furniture',
    defaultMessage: 'Furniture'
  },
  activities: {
    id: 'shared.event_faq.section.event_activities',
    defaultMessage: 'Event Activities'
  },
  av_music: {
    id: 'shared.event_faq.section.av_music',
    defaultMessage: 'AV and Music'
  },
  rules: {
    id: 'shared.event_faq.section.rules',
    defaultMessage: 'Event Rules'
  }
});

const {
  serving_utensils_subheader,
  place_setting_tooltip,
  cups_tooltip,
  wine_glasses_tooltip,
  tables_tooltip,
  chairs_tooltip,
  soft_seating_tooltip,
  restroom_tooltip } = miscEventFaqDefinitions;

const {
  place_settings_subheader,
  other_food_rules_placeholder,
  other_food_prep_rules_placeholder,
  alcohol_consumption_placeholder,
  other_av_equipment_placeholder,
  other_activities_placeholder,
  other_prohibited_activities_placeholder,
  other_event_rules_placeholder
} = miscEventFaqDefinitions;

const eventFaqDefinition = defineMessages({
  food_and_beverage: {
    [EVENT_FAQ_CONST.FOOD_BEVERAGE_RULES]: defineMessages({
      [EVENT_FAQ_ANSWERS_CONST.OUTSIDE_FOOD_BEVERAGE_ALLOWED]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.outside_food_beverage_allowed',
        defaultMessage: 'Outside food and non-alcoholic beverages allowed'
      },
      [EVENT_FAQ_ANSWERS_CONST.ON_SITE_FOOD_BEVERAGE]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.on_site_food_beverage',
        defaultMessage: 'Food and non-alcoholic beverages are available for purchase on-site only'
      },
      [EVENT_FAQ_ANSWERS_CONST.LICENSED_FOOD_BEVERAGE]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.licensed_food_beverage',
        defaultMessage: 'Food and non-alcoholic beverages must be provided by a licensed vendor'
      },
      [EVENT_FAQ_ANSWERS_CONST.NO_OUTSIDE_FOOD_BEVERAGE]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.no_outside_food_beverage',
        defaultMessage: 'No outside food, guests are allowed to bring specialty items (ie: cakes, dessert, etc)'
      },
      [EVENT_FAQ_ANSWERS_CONST.OTHER_FOOD_AND_BEVERAGE_RULES]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.other_food_and_beverage_rules',
        defaultMessage: 'Other',
        placeholder: other_food_rules_placeholder
      }
    }),
    [EVENT_FAQ_CONST.FOOD_PREP_RULES]: defineMessages({
      [EVENT_FAQ_ANSWERS_CONST.COOKING_AND_PREP_ALLOWED]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.cooking_and_prep_allowed',
        defaultMessage: 'Cooking and food preparation is allowed on-site'
      },
      [EVENT_FAQ_ANSWERS_CONST.ONLY_FOOD_PREP]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.only_food_prep',
        defaultMessage: 'Only food preparation is allowed on-site'
      },
      [EVENT_FAQ_ANSWERS_CONST.NO_COOKING_ALLOWED]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.no_cooking_allowed',
        defaultMessage: 'No cooking is allowed on-site'
      },
      [EVENT_FAQ_ANSWERS_CONST.OTHER_FOOD_PREP_RULES]: {
        id: 'shared.food_and_beverage.event_faq_answers_const.other_food_prep_rules',
        defaultMessage: 'Other',
        placeholder: other_food_prep_rules_placeholder
      }
    }),
    kitchen_setups_provided: defineMessages({
      [EVENT_FAQ_CONST.FULL_KITCHEN]: {
        id: 'shared.food_and_beverage.full_kitchen',
        defaultMessage: 'Full kitchen'
      },
      [EVENT_FAQ_CONST.COMMERCIAL_KITCHEN]: {
        id: 'shared.food_and_beverage.event_faq_const.commercial_kitchen',
        defaultMessage: 'Commercial Kitchen'
      },
      [EVENT_FAQ_CONST.KITCHENETTE]: {
        id: 'shared.food_and_beverage.event_faq_const.kitchenette',
        defaultMessage: 'Kitchenette'
      },
      [EVENT_FAQ_CONST.PREP_AREA]: {
        id: 'shared.food_and_beverage.event_faq_const.prep_area',
        defaultMessage: 'Prep area'
      }
    }),
    kitchen_amenities_provided: defineMessages({
      [EVENT_FAQ_CONST.PLACE_SETTINGS]: {
        id: 'shared.food_and_beverage.event_faq_const.place_settings',
        defaultMessage: 'Place settings',
        subheader: place_settings_subheader,
        tooltip: place_setting_tooltip
      },
      [EVENT_FAQ_CONST.SERVING_UTENSILS]: {
        id: 'shared.food_and_beverage.event_faq_const.serving_utensils',
        defaultMessage: 'Serving utensils',
        subheader: serving_utensils_subheader
      },
      [EVENT_FAQ_CONST.CUPS_GLASSES]: {
        id: 'shared.food_and_beverage.event_faq_const.cups_glasses',
        defaultMessage: 'Cups',
        tooltip: cups_tooltip
      },
      [EVENT_FAQ_CONST.WINE_GLASSES]: {
        id: 'shared.food_and_beverage.event_faq_const.wine_glasses',
        defaultMessage: 'Wine glasses',
        tooltip: wine_glasses_tooltip
      }
    })
  },
  alcohol_beverages: {
    [EVENT_FAQ_CONST.BRING_ALCOHOL]: defineMessages({
      [EVENT_FAQ_ANSWERS_CONST.BRING_ALCOHOL_ALLOWED]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.bring_alcohol_allowed',
        defaultMessage: 'Guests can bring their own alcohol'
      },
      [EVENT_FAQ_ANSWERS_CONST.BRING_ALCOHOL_NOT_ALLOWED]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.bring_alcohol_not_allowed',
        defaultMessage: 'Guests are not allowed to bring their own alcohol'
      },
      [EVENT_FAQ_ANSWERS_CONST.NO_ALCOHOL_ALLOWED]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.no_alcohol_allowed',
        defaultMessage: 'Alcohol consumption is not allowed in the space'
      }
    }),
    [EVENT_FAQ_CONST.BRING_ALCOHOL_RULES]: defineMessages({
      [EVENT_FAQ_ANSWERS_CONST.ANY_ALCOHOL]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.any_alcohol',
        defaultMessage: 'Any type of alcohol'
      },
      [EVENT_FAQ_ANSWERS_CONST.LIQUOR_NOT_ALLOWED]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.liquor_not_allowed',
        defaultMessage: 'Beer, wine, and seltzers only. Liquor is not allowed.'
      },
      [EVENT_FAQ_ANSWERS_CONST.LICENSED_LIQUOR]: {
        id: 'shared.alcohol_beverages.event_faq_answers_const.licensed_liquor',
        defaultMessage: 'Beer, wine, and seltzers only. Liquor must be served by a licensed vendor.'
      }
    }),
    alcohol_consumption_options: defineMessages({
      [EVENT_FAQ_CONST.ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR]: {
        id: 'shared.alcohol_beverages.event_faq_const.alcohol_must_be_served_by_licensed_vendor',
        defaultMessage: 'All alcohol must be served by a licensed vendor'
      },
      [EVENT_FAQ_CONST.CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR]: {
        id: 'shared.alcohol_beverages.event_faq_const.can_purchase_alcohol_at_full_service_bar',
        defaultMessage: 'Alcohol is available for purchase at our full service bar on-site'
      },
      [EVENT_FAQ_CONST.CAN_PURCHASE_BEER_WINE_AT_BAR]: {
        id: 'shared.alcohol_beverages.event_faq_const.can_purchase_beer_wine_at_bar',
        defaultMessage: 'Beer and wine is available for purchase at our bar on-site'
      },
      [EVENT_FAQ_CONST.OTHER_ALCOHOL_CONSUMPTION_RULES]: {
        id: 'shared.alcohol_beverages.event_faq_const.other_alcohol_consumption_rules',
        defaultMessage: 'Other rules',
        placeholder: alcohol_consumption_placeholder
      }
    })
  },
  furniture: defineMessages({
    [EVENT_FAQ_CONST.TABLES]: {
      id: 'shared.furniture_form.event_faq_const.tables',
      defaultMessage: 'Dining tables, cocktail tables, etc',
      tooltip: tables_tooltip
    },
    [EVENT_FAQ_CONST.CHAIRS]: {
      id: 'shared.furniture_form.event_faq_const.chairs',
      defaultMessage: 'Dining chairs, stools, etc',
      tooltip: chairs_tooltip
    },
    [EVENT_FAQ_CONST.SOFT_SEATING]: {
      id: 'shared.furniture_form.event_faq_const.soft_seating',
      defaultMessage: 'Sofas, lounge chairs, etc',
      tooltip: soft_seating_tooltip
    }
  }),
  restroom: defineMessages({
    [EVENT_FAQ_CONST.STANDARD_RESTROOMS]: {
      id: 'shared.restroom_form.how_many_restrooms',
      defaultMessage: 'Restrooms on-site',
      tooltip: restroom_tooltip
    },
    [EVENT_FAQ_CONST.ADA_RESTROOMS]: {
      id: 'shared.restroom_form.has_ada_restrooms',
      defaultMessage: 'ADA accessible restrooms'
    },
    [EVENT_FAQ_CONST.NURSING_ROOMS]: {
      id: 'shared.restroom_form.has_nursing_rooms',
      defaultMessage: 'Nursing or parent room'
    }
  }),
  av_music: {
    provided_av_equipment: defineMessages({
      [EVENT_FAQ_CONST.MICROPHONES]: {
        id: 'shared.av_music_form.event_faq_const.microphones',
        defaultMessage: 'Microphones'
      },
      [EVENT_FAQ_CONST.PROJECTOR]: {
        id: 'shared.av_music_form.event_faq_const.projector',
        defaultMessage: 'Projector'
      },
      [EVENT_FAQ_CONST.SPEAKERS]: {
        id: 'shared.av_music_form.event_faq_const.speakers',
        defaultMessage: 'Speakers'
      },
      [EVENT_FAQ_CONST.TELEVISION]: {
        id: 'shared.av_music_form.event_faq_const.television',
        defaultMessage: 'Television'
      },
      [EVENT_FAQ_CONST.MISC_AV_EQUIPMENT]: {
        id: 'shared.av_music_form.event_faq_const.misc_av_equipment',
        defaultMessage: 'Other AV equipment',
        placeholder: other_av_equipment_placeholder
      }
    }),
    av_equipment_rules: defineMessages({
      [EVENT_FAQ_CONST.IS_DJ_ALLOWED]: {
        id: 'shared.av_music_form.event_faq_const.is_dj_allowed',
        defaultMessage: 'DJs are allowed'
      },
      [EVENT_FAQ_CONST.IS_LIVE_MUSIC_ALLOWED]: {
        id: 'shared.av_music_form.event_faq_const.is_live_music_allowed',
        defaultMessage: 'Live music is allowed'
      },
      [EVENT_FAQ_CONST.IS_AMPLIFIED_MUSIC_ALLOWED]: {
        id: 'shared.av_music_form.event_faq_const.is_amplified_music_allowed',
        defaultMessage: 'Amplified music is allowed'
      },
      [EVENT_FAQ_CONST.IS_CONNECTED_AMPLIFIED_MUSIC_ALLOWED]: {
        id: 'shared.av_music_form.event_faq_const.is_connected_amplified_music_allowed',
        defaultMessage: 'Guests may amplify music by connecting a personal device to speakers within the space'
      }
    })
  },
  event_activities: {
    activities_provided: defineMessages({
      [EVENT_FAQ_CONST.ARCADE_GAMES]: {
        id: 'shared.event_activities.event_faq_const.arcade_games',
        defaultMessage: 'Arcade Games'
      },
      [EVENT_FAQ_CONST.DARTS]: {
        id: 'shared.event_activities.event_faq_const.darts',
        defaultMessage: 'Darts'
      },
      [EVENT_FAQ_CONST.KARAOKE]: {
        id: 'shared.event_activities.event_faq_const.karaoke',
        defaultMessage: 'Karaoke'
      },
      [EVENT_FAQ_CONST.PING_PONG_TABLE]: {
        id: 'shared.event_activities.event_faq_const.ping_pong_table',
        defaultMessage: 'Ping pong table'
      },
      [EVENT_FAQ_CONST.POOL_TABLE]: {
        id: 'shared.event_activities.event_faq_const.pool_table',
        defaultMessage: 'Pool table'
      },
      [EVENT_FAQ_CONST.SHUFFLEBOARD]: {
        id: 'shared.event_activities.event_faq_const.shuffleboard',
        defaultMessage: 'Shuffleboard'
      },
      [EVENT_FAQ_CONST.MISC_ACTIVITIES]: {
        id: 'shared.event_activities.event_faq_const.misc_activities',
        defaultMessage: 'Other activities',
        placeholder: other_activities_placeholder
      }
    }),
    prohibited_activities: defineMessages({
      [EVENT_FAQ_CONST.IS_21ST_BDAY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_21st_bday_prohibited',
        defaultMessage: '21st birthday party'
      },
      [EVENT_FAQ_CONST.IS_AFTER_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_after_party_prohibited',
        defaultMessage: 'After party'
      },
      [EVENT_FAQ_CONST.IS_ALBUM_RELEASE_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_album_release_party_prohibited',
        defaultMessage: 'Album release party'
      },
      [EVENT_FAQ_CONST.IS_BACHELOR_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_bachelorette_bachelor_party_prohibited',
        defaultMessage: 'Bachelorette and bachelor parties'
      },
      [EVENT_FAQ_CONST.IS_CONCERT_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_concerts_performances_prohibited',
        defaultMessage: 'Concert or live performances'
      },
      [EVENT_FAQ_CONST.IS_FESTIVAL_HOLIDAY_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_holiday_parties_prohibited',
        defaultMessage: 'Holiday parties'
      },
      [EVENT_FAQ_CONST.IS_INFLUENCER_EVENT_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_influencer_event_prohibited',
        defaultMessage: 'Influencer event'
      },
      [EVENT_FAQ_CONST.IS_KIDS_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_kids_birthday_party_prohibited',
        defaultMessage: 'Kids birthday party'
      },
      [EVENT_FAQ_CONST.IS_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_party_prohibited',
        defaultMessage: 'Party'
      },
      [EVENT_FAQ_CONST.IS_SPORTS_VIEWING_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_sports_viewing_party_prohibited',
        defaultMessage: 'Sporting event viewing party'
      },
      [EVENT_FAQ_CONST.IS_WEDDING_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_wedding_prohibited',
        defaultMessage: 'Wedding'
      },
      [EVENT_FAQ_CONST.OTHER_PROHIBITED_ACTIVITY]: {
        id: 'shared.event_activities.event_faq_const.other_prohibited_activity',
        defaultMessage: 'Other activities',
        placeholder: other_prohibited_activities_placeholder
      }
    }),
    prohibited_activities_info: defineMessages({
      [EVENT_FAQ_CONST.IS_FESTIVAL_HOLIDAY_PARTY_PROHIBITED]: {
        id: 'shared.event_activities.event_faq_const.is_holiday_parties_info',
        defaultMessage: 'Includes: Halloween Party, Thanksgiving Party, Holiday Party, Christmas Party, New Years Eve Party'
      }
    })
  },
  event_rules: defineMessages({
    [EVENT_FAQ_CONST.HAS_QUIET_HOURS]: {
      id: 'shared.event_rules_form.event_faq_const.has_quiet_hours',
      defaultMessage: 'No restrictions on noise during your space\'s operating hours'
    },
    [EVENT_FAQ_CONST.LOUD_MUSIC_ENDS_BY]: {
      id: 'shared.event_rules_form.event_faq_const.loud_music_ends_by',
      defaultMessage: 'Any loud music or noise must end by'
    },
    rules_restriction: defineMessages({
      [EVENT_FAQ_CONST.IS_GLITTER_ALLOWED]: {
        id: 'shared.event_rules_form.event_faq_const.is_glitter_allowed',
        defaultMessage: 'Confetti or glitter are allowed'
      },
      [EVENT_FAQ_CONST.IS_HIRED_SECURITY_REQUIRED]: {
        id: 'shared.event_rules_form.event_faq_const.is_hired_security_required',
        defaultMessage: 'Large events require hired security'
      },
      [EVENT_FAQ_CONST.ARE_OPEN_FLAMES_ALLOWED]: {
        id: 'shared.event_rules_form.event_faq_const.are_open_flames_allowed',
        defaultMessage: 'Open flames are allowed'
      },
      [EVENT_FAQ_CONST.ARE_PETS_ALLOWED]: {
        id: 'shared.event_rules_form.event_faq_const.are_pets_allowed',
        defaultMessage: 'Pets are allowed'
      },
      [EVENT_FAQ_CONST.IS_SMOKING_ALLOWED]: {
        id: 'shared.event_rules_form.event_faq_const.is_smoking_allowed',
        defaultMessage: 'Smoking is allowed'
      },
      [EVENT_FAQ_CONST.ARE_TICKETED_EVENTS_ALLOWED]: {
        id: 'shared.event_rules_form.event_faq_const.are_ticketed_events_allowed',
        defaultMessage: 'Ticketed events are allowed'
      },
      [EVENT_FAQ_CONST.OTHER_EVENT_RULES]: {
        id: 'shared.event_rules_form.event_faq_const.other_event_rules',
        defaultMessage: 'Other event rules',
        placeholder: other_event_rules_placeholder
      }
    }),
    [EVENT_FAQ_CONST.HAS_FLEXIBLE_HOURS]: defineMessages({
      [EVENT_FAQ_ANSWERS_CONST.HOURS_ADJUSTED_UPON_REQUEST]: {
        id: 'shared.event_rules_form.event_faq_answers_const.hours_adjusted_upon_request',
        defaultMessage: 'Yes, hours may be adjusted upon request'
      },
      [EVENT_FAQ_ANSWERS_CONST.HAS_NO_FLEXIBLE_HOURS]: {
        id: 'shared.event_rules_form.event_faq_answers_const.has_no_flexible_hours',
        defaultMessage: 'No, hours are not flexible'
      }
    })
  })
});

const formattedMiscEventFaqDefinitions = {
  amenities_note: (
    <FormattedMessage
      id="shared.faq_form.amenities_note"
      defaultMessage="Amenities available for a fee must be added as an add-on item {addOnLink}"
      values={{
        addOnLink: (
          <a href="/hosting/add-ons" target="_blank" rel="noopener noreferrer" className="text-gray-600 underline">
            <FormattedMessage
              id="shared.event_activities_form.amenities_note_link"
              defaultMessage="here"
            />
          </a>
        )
      }}
    />
  ),
  event_rules_info: (
    <FormattedMessage
      id='shared.faq_form.event_rules_info'
      defaultMessage='All events are subject to the {socialEventPolicyLink}. Event rules are considered part of your host rules which guests must agree to before booking your space.'
      values={{
        socialEventPolicyLink: (
          <a
            href="/legal/terms/social-event-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: '#886A2F' }}
          >
            <FormattedMessage
              id="shared.event_activities_form.social_event_policy"
              defaultMessage="Peerspace Social Event Policy"
            />
          </a>
        )
      }}
    />
  ),
  loud_music_warning: (
    <FormattedMessage
      id='shared.faq_form.loud_music_warning'
      defaultMessage='* All social events at {residentialSpaces} spaces must start prior to 9:00 pm and end no later than 12:00 am (local time), inclusive of set-up and clean-up time. {socialEventPolicyLink}'
      values={{
        residentialSpaces: (
          <span className="heavier">
            <FormattedMessage
              id="shared.event_activities_form.loud_music_warning_residential"
              defaultMessage="residential"
            />
          </span>
        ),
        socialEventPolicyLink: (
          <a
            href="/legal/terms/social-event-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 underline"
          >
            <FormattedMessage
              id="shared.event_activities_form.loud_music_warning_learn_more"
              defaultMessage="Learn more"
            />
          </a>
        )
      }}
    />
  ),
  residential_warning: (
    <FormattedMessage
      id='shared.faq_form.residential_warning'
      defaultMessage='* {residentialSpaces} are not allowed to hold ticketed events. {socialEventPolicyLink}'
      values={{
        residentialSpaces: (
          <span className="heavier">
            <FormattedMessage
              id="shared.event_activities_form.residential_warning_heavy"
              defaultMessage="Residential Spaces"
            />
          </span>
        ),
        socialEventPolicyLink: (
          <a
            href="/legal/terms/social-event-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 underline"
          >
            <FormattedMessage
              id="shared.event_activities_form.residential_warning_learn_more"
              defaultMessage="Learn more"
            />
          </a>
        )
      }}
    />
  )
};

export { eventFaqDefinition, miscEventFaqDefinitions, formattedMiscEventFaqDefinitions, category_labels, subtitles };
